import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import api from '../services/api';
import { ResumeJourney, StageQuestions } from '../types';

interface JourneyContextType {
  currentJourney: ResumeJourney | null;
  setCurrentJourney: (journey: ResumeJourney | null) => void;
  currentQuestions: StageQuestions | null;
  isLoading: boolean;
  createNewJourney: () => Promise<void>;
  sendMessage: (message: string | Blob) => Promise<{ aiResponse: string; transcribedText?: string }>;
  moveToNextStage: () => Promise<void>;
  refreshJourney: () => Promise<void>;
  updateJourneyData: (data: any) => Promise<void>;
  updateCurrentStage: (stage: number) => void;
}

const JourneyContext = createContext<JourneyContextType | undefined>(undefined);

export const JourneyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentJourney, setCurrentJourney] = useState<ResumeJourney | null>(null);
  const [currentQuestions, setCurrentQuestions] = useState<StageQuestions | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const createNewJourney = async () => {
    try {
      setIsLoading(true);
      const response = await api.createJourney();
      setCurrentJourney(response.journey);
      setCurrentQuestions(response.questions);
      
      // Return the journey so callers can use it immediately
      return response.journey;
    } catch (error: any) {
      console.error('Failed to create journey:', error);
      throw new Error(error.response?.data?.message || 'Failed to create journey');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (message: string | Blob): Promise<{ aiResponse: string; transcribedText?: string }> => {
    if (!currentJourney) throw new Error('No active journey');

    try {
      setIsLoading(true);
      
      let response;
      if (typeof message === 'string') {
        // Text message
        response = await api.sendMessage(currentJourney.id, message);
      } else {
        // Audio blob - send as multipart form data
        response = await api.sendAudioMessage(currentJourney.id, message);
      }
      
      // Refresh journey to get updated conversation history
      await refreshJourney();
      
      return {
        aiResponse: response.message,
        transcribedText: response.transcribedText // Will be present for audio messages
      };
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const moveToNextStage = async () => {
    if (!currentJourney) throw new Error('No active journey');

    try {
      setIsLoading(true);
      const response = await api.moveToNextStage(currentJourney.id);
      
      // Update journey with new stage and any extracted data (for Review stage)
      setCurrentJourney({
        ...currentJourney,
        currentStage: response.currentStage,
        collectedData: response.collectedData || currentJourney.collectedData,
      });
      setCurrentQuestions(response.questions);
    } catch (error) {
      console.error('Failed to move to next stage:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshJourney = useCallback(async () => {
    if (!currentJourney) return;

    try {
      const journey = await api.getJourney(currentJourney.id);
      console.log('[RefreshJourney] Fetched updated journey:', journey);
      setCurrentJourney(journey);
      return journey;
    } catch (error) {
      console.error('Failed to refresh journey:', error);
      throw error;
    }
  }, [currentJourney]);

  const updateJourneyData = async (data: any) => {
    if (!currentJourney) throw new Error('No active journey');

    try {
      setIsLoading(true);
      
      // Merge new data with existing data
      const updatedData = {
        ...currentJourney.collectedData,
        ...data,
      };
      
      // Transform data to match backend expectations
      const transformedData = transformDataForBackend(updatedData);
      
      // Update the local state optimistically
      setCurrentJourney({
        ...currentJourney,
        collectedData: updatedData,
      });
      
      // Persist to backend
      await api.updateJourneyCollectedData(currentJourney.id, transformedData);
    } catch (error) {
      console.error('Failed to update journey data:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCurrentStage = (stage: number) => {
    if (!currentJourney) return;
    setCurrentJourney({
      ...currentJourney,
      currentStage: stage,
    });
  };

  // Transform frontend data to backend format
  const transformDataForBackend = (data: any) => {
    const transformed = { ...data };
    
    // Transform education honors from string to array
    if (transformed.education) {
      transformed.education = transformed.education.map((edu: any) => ({
        ...edu,
        honors: edu.honors 
          ? (typeof edu.honors === 'string' ? (edu.honors.trim() ? [edu.honors] : []) : edu.honors)
          : []
      }));
    }
    
    return transformed;
  };

  return (
    <JourneyContext.Provider
      value={{
        currentJourney,
        setCurrentJourney,
        currentQuestions,
        isLoading,
        createNewJourney,
        sendMessage,
        moveToNextStage,
        refreshJourney,
        updateJourneyData,
        updateCurrentStage,
      }}
    >
      {children}
    </JourneyContext.Provider>
  );
};

export const useJourney = () => {
  const context = useContext(JourneyContext);
  if (context === undefined) {
    throw new Error('useJourney must be used within a JourneyProvider');
  }
  return context;
};

